import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Info, 
  Rocket, 
  TestTube, 
  Users, 
  TrendingUp, 
  BarChart3,
  Activity,
  Target,
  Clock,
} from 'lucide-react';

interface StrategyLogic {
  entry: {
    indicator: string;
    condition: string;
    value: number;
  };
  confirmation?: {
    indicator: string;
    condition: string;
    value: number;
  };
  action: string;
  sl: number;
  tp: number;
}

export interface StrategyCardData {
  id: string;
  name: string;
  description: string;
  accuracy: string;
  live_users: number;
  category: string;
  logic: StrategyLogic;
  risk_level: 'Low' | 'Medium' | 'High';
  timeframe: string;
  is_predefined: boolean;
}

interface StrategyCardProps {
  strategy: StrategyCardData;
  onInfo: (strategy: StrategyCardData) => void;
  onDeploy: (strategy: StrategyCardData) => void;
  onTest: (strategy: StrategyCardData) => void;
}

export const StrategyCard = ({ strategy, onInfo, onDeploy, onTest }: StrategyCardProps) => {
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'text-accent-green';
      case 'Medium': return 'text-accent-amber';
      case 'High': return 'text-accent-red';
      default: return 'text-muted-foreground';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'reversal': return TrendingUp;
      case 'momentum': return Activity;
      case 'scalping': return BarChart3;
      default: return Target;
    }
  };

  const CategoryIcon = getCategoryIcon(strategy.category);
  const rrRatio = strategy.logic.sl > 0 ? (strategy.logic.tp / strategy.logic.sl).toFixed(1) : '—';

  return (
    <Card className="bg-card/40 border-border/20 hover:border-border/40 rounded-xl transition-colors min-h-[260px]">
      <CardContent className="p-5 space-y-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <CategoryIcon className="h-4 w-4 text-primary shrink-0" />
            <h3 className="font-semibold text-sm truncate font-heading">{strategy.name}</h3>
          </div>
          <Badge
            variant={strategy.is_predefined ? 'default' : 'outline'}
            className="text-[10px] shrink-0 ml-2"
          >
            {strategy.is_predefined ? 'Verified' : 'Custom'}
          </Badge>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-2">
          <div className="rounded-lg bg-muted/20 p-2 text-center">
            <div className="text-sm font-bold text-accent-green font-mono-data">{strategy.accuracy}</div>
            <p className="text-[10px] text-muted-foreground">Accuracy</p>
          </div>
          <div className="rounded-lg bg-muted/20 p-2 text-center">
            <div className="text-sm font-bold flex items-center justify-center gap-1 font-mono-data">
              <Users className="h-3 w-3 text-muted-foreground" />
              {strategy.live_users}
            </div>
            <p className="text-[10px] text-muted-foreground">Live Users</p>
          </div>
          <div className="rounded-lg bg-muted/20 p-2 text-center">
            <div className="text-sm font-bold flex items-center justify-center gap-1 font-mono-data">
              <Clock className="h-3 w-3 text-muted-foreground" />
              {strategy.timeframe}
            </div>
            <p className="text-[10px] text-muted-foreground">Timeframe</p>
          </div>
          <div className="rounded-lg bg-muted/20 p-2 text-center">
            <div className={`text-sm font-bold font-mono-data ${getRiskColor(strategy.risk_level)}`}>
              {strategy.risk_level}
            </div>
            <p className="text-[10px] text-muted-foreground">Risk</p>
          </div>
        </div>

        <Separator className="bg-border/20" />

        {/* Logic details */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono-data">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Entry:</span>
            <span className="text-foreground">{strategy.logic.entry.indicator} {strategy.logic.entry.condition} {strategy.logic.entry.value}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">SL:</span>
            <span className="text-accent-red">{strategy.logic.sl}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Confirm:</span>
            <span className="text-foreground">
              {strategy.logic.confirmation
                ? `${strategy.logic.confirmation.indicator} ${strategy.logic.confirmation.condition}`
                : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">TP:</span>
            <span className="text-accent-green">{strategy.logic.tp}%</span>
          </div>
          <div />
          <div className="flex justify-between">
            <span className="text-muted-foreground">R:R:</span>
            <span className="text-foreground">1:{rrRatio}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-auto pt-1 space-y-2 md:space-y-0">
          {/* Deploy full-width on mobile */}
          <Button
            size="sm"
            className="text-xs h-9 w-full md:hidden bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => onDeploy(strategy)}
          >
            <Rocket className="h-3.5 w-3.5 mr-1" />
            Deploy Strategy
          </Button>
          <div className="grid grid-cols-2 md:hidden gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => onInfo(strategy)}>
              <Info className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => onTest(strategy)}>
              <TestTube className="h-3.5 w-3.5" />
            </Button>
          </div>
          {/* Desktop: 3-col */}
          <div className="hidden md:grid grid-cols-3 gap-2">
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => onInfo(strategy)}>
              <Info className="h-3.5 w-3.5 mr-1" /> Info
            </Button>
            <Button size="sm" className="text-xs h-8 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onDeploy(strategy)}>
              <Rocket className="h-3.5 w-3.5 mr-1" /> Deploy
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => onTest(strategy)}>
              <TestTube className="h-3.5 w-3.5 mr-1" /> Test
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
